/*
 * <nl-hero-videos data-label-pause data-label-play> (snippets/nl-home-hero-rail.liquid)
 *   [data-hero-card] > .nl-hv > video[data-hero-video] (preload none, muted, loop, playsinline, <source> list)
 *   button[data-hero-pause] (one control for all hero videos, WCAG 2.2.2)
 *
 * Playback policy:
 *   - nothing is fetched before the window load event, so the hero text and posters win the first paint (LCP)
 *   - a video plays only while its card is at least half visible; the IntersectionObserver also respects the
 *     rail's horizontal clipping, so cards scrolled out of the rail pause too
 *   - pause when the tab is hidden; never autoplay under prefers-reduced-motion, the theme motion setting,
 *     Save-Data or a 2G connection (posters stay, the pause button turns into a play button)
 *   - the browser picks the <source> (SD-480p up to 1023 px, then HD-720p, then the original) and moves on by
 *     itself when a derived rendition fails
 *   - final T11 (M21): up to 1023 px only the most visible card plays, so a tablet showing two or three cards
 *     fetches one short SD loop instead of several HD ones before the first scroll
 *   - card state: data-started once the first frame plays (poster cross fade in CSS)
 */
(() => {
  'use strict';

  if (customElements.get('nl-hero-videos')) return;

  const reducedMotion = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    document.documentElement.classList.contains('nl-motion-off') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const lowData = () => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    return Boolean(c.saveData) || /(^|-)2g$/.test(String(c.effectiveType || ''));
  };

  const mqSingle = window.matchMedia('(max-width: 1023px)');

  const pageLoaded = () =>
    new Promise((resolve) => {
      if (document.readyState === 'complete') resolve();
      else window.addEventListener('load', () => resolve(), { once: true });
    });

  class HeroVideos extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.items = Array.from(this.querySelectorAll('video[data-hero-video]')).map((video) => ({
        video,
        card: video.closest('[data-hero-card]') || video.parentElement,
        visible: false,
        ratio: 0,
      }));
      this.button = this.querySelector('[data-hero-pause]');
      if (!this.items.length) return;

      this.auto = !reducedMotion() && !lowData();
      this.userPaused = !this.auto;
      this.ready = false;

      this.items.forEach((item) => {
        item.video.muted = true;
        item.video.addEventListener('playing', () => item.card.setAttribute('data-started', ''));
      });

      if (this.button) {
        this.button.hidden = false;
        this.button.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.ready = true;
          this.syncButton();
          this.evaluateAll();
        });
        this.syncButton();
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const item = this.items.find((candidate) => candidate.card === entry.target);
              if (item) {
                item.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
                item.visible = item.ratio >= 0.5;
              }
            });
            this.evaluateAll();
          },
          { threshold: [0, 0.5, 0.75, 0.9, 1] }
        );
        this.items.forEach((item) => this.observer.observe(item.card));
      }

      this.onVisibility = () => this.evaluateAll();
      document.addEventListener('visibilitychange', this.onVisibility);

      pageLoaded().then(() => {
        // Short idle gap after load so late images and fonts settle before the first video request
        window.setTimeout(() => {
          this.ready = true;
          this.evaluateAll();
        }, 250);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      (this.items || []).forEach((item) => item.video.pause());
      this._bound = false;
    }

    syncButton() {
      if (!this.button) return;
      const paused = this.userPaused;
      this.button.setAttribute('aria-pressed', String(paused));
      this.button.setAttribute('aria-label', paused ? this.dataset.labelPlay || 'Play' : this.dataset.labelPause || 'Pause');
      this.button.toggleAttribute('data-paused', paused);
    }

    evaluateAll() {
      const single = mqSingle.matches;
      let lead = null;
      if (single) {
        this.items.forEach((item) => {
          if (item.visible && (!lead || item.ratio > lead.ratio)) lead = item;
        });
      }
      this.items.forEach((item) => {
        const wanted = this.ready && !this.userPaused && item.visible && (!single || item === lead) && !document.hidden;
        if (wanted) {
          const promise = item.video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        } else if (!item.video.paused) {
          item.video.pause();
        }
      });
    }
  }

  customElements.define('nl-hero-videos', HeroVideos);
})();
